import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCaretDown, faColumns } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropdownItemLink } from 'components/dropdown/styled';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { useKeyPress } from 'custom_hooks/useKeyPress';
import { usePopup } from 'custom_hooks/usePopup';
import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { getCorporationSpecificDashboardRoute } from 'utils/RouteHelper';

export type DashboardId =
    | 'desk-activity-overview'
    | 'deployment-and-value'
    | 'value-by-location';

export type DashboardOption = {
    id: DashboardId;
    label: string;
    icon?: IconDefinition;
};

export const DASHBOARDS: DashboardOption[] = [
    { id: 'deployment-and-value', label: 'Deployment and Value' },
    { id: 'desk-activity-overview', label: 'Desk Activity', icon: faColumns },
    { id: 'value-by-location', label: 'Value by Location' },
];

const VISIBLE_DASHBOARDS = DASHBOARDS.filter((d) => d.id !== 'value-by-location');

const TriggerButton = styled.button<{ $isOpen: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.8rem;
    border: none;
    border-radius: 6px;
    background-color: ${({ $isOpen }) =>
        $isOpen ? tokens.background.subtle : 'transparent'};
    color: ${tokens.text.secondary};
    font-size: 2rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        background-color: ${tokens.background.subtle};
        color: ${tokens.text.primary};
    }

    &:focus-visible {
        outline: 2px solid ${tokens.nav.border};
        outline-offset: 2px;
    }
`;

const IconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 1.8rem;
`;

const ChevronIcon = styled(FontAwesomeIcon)<{ $isOpen: boolean }>`
    font-size: 1.2rem;
    margin-left: 0.2rem;
    transition: transform 0.2s ease;
    transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
`;

const PopupContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding: 0.8rem;
    min-width: 24rem;
`;

const DashboardItemLink = styled(DropdownItemLink)<{ $isActive: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.8rem 1.2rem;
    border-radius: 6px;
    border: 1px solid transparent;
    background-color: ${({ $isActive }) =>
        $isActive ? tokens.background.subtle : 'transparent'};
    font-size: 1.4rem;
    font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
    transition: all 0.15s ease;

    &:hover,
    &:focus-visible {
        border-color: ${tokens.nav.border};
        background-color: ${tokens.background.subtle};
    }
`;

const ItemIconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${tokens.text.secondary};
    font-size: 1.6rem;
    width: 2rem;
`;

export type DashboardSwitcherDropdownProps = {
    corporationId: string;
    currentDashboard: DashboardId;
    className?: string;
};

export const DashboardSwitcherDropdown = memo(function DashboardSwitcherDropdown({
    corporationId,
    currentDashboard,
    className,
}: DashboardSwitcherDropdownProps) {
    const { isOpen, close, toggleOpen, triggerRef, popupRef } = usePopup({
        closeOnClickOutside: true,
    });

    useKeyPress('Escape', () => {
        if (isOpen) {
            close();
        }
    });

    const currentDashboardOption = useMemo(
        () => DASHBOARDS.find((d) => d.id === currentDashboard),
        [currentDashboard]
    );

    return (
        <>
            <TriggerButton
                ref={triggerRef}
                onClick={toggleOpen}
                $isOpen={isOpen}
                className={className}
                aria-label="Switch dashboard"
                aria-expanded={isOpen}
            >
                {currentDashboardOption?.icon && (
                    <IconWrapper>
                        <FontAwesomeIcon icon={currentDashboardOption.icon} />
                    </IconWrapper>
                )}
                {currentDashboardOption?.label || 'Dashboard'}
                <ChevronIcon icon={faCaretDown} $isOpen={isOpen} />
            </TriggerButton>

            {isOpen && (
                <StatelessPopup
                    ref={popupRef}
                    anchor={triggerRef.current}
                    anchorAlign={{
                        horizontal: 'left',
                        vertical: 'bottom',
                    }}
                    popupAlign={{
                        horizontal: 'left',
                        vertical: 'top',
                    }}
                    onClose={close}
                    noPadding
                >
                    <PopupContent>
                        {VISIBLE_DASHBOARDS.map((dashboard) => {
                            const route = getCorporationSpecificDashboardRoute(
                                corporationId,
                                dashboard.id
                            );
                            const isActive = dashboard.id === currentDashboard;

                            return (
                                <DashboardItemLink
                                    key={dashboard.id}
                                    to={route}
                                    $isActive={isActive}
                                    aria-current={isActive ? 'page' : undefined}
                                >
                                    {dashboard.icon ? (
                                        <ItemIconWrapper>
                                            <FontAwesomeIcon icon={dashboard.icon} />
                                        </ItemIconWrapper>
                                    ) : (
                                        <ItemIconWrapper />
                                    )}
                                    {dashboard.label}
                                </DashboardItemLink>
                            );
                        })}
                    </PopupContent>
                </StatelessPopup>
            )}
        </>
    );
});
